import { useGateValue } from "@statsig/react-bindings";
import { Box, Flex, Heading } from "@radix-ui/themes";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import FeaturedAlbumsSection from "./FeaturedAlbumsSection";
import { MarketplaceTabs } from "./MarketplaceTabs";
import AlbumSliderSection from "./AlbumSliderSection";
import { useAlbumsControllerTrendingAlbums } from "@/lib/lutely-api/gen/albums/albums";
import { useAlbumsInfiniteList } from "@/lib/lutely-api/hooks/useAlbumsInfiniteList";
import { MarketplaceSearch, MarketplaceUpdates } from "./types";
import { marketplaceTokens } from "@/lib/tokens";
import AlbumCard from "../ui/AlbumCard";
import InfiniteScrollSentinel from "../ui/InfiniteScrollSentinel";
import SortDropdown from "./SortDropdown";
import { mapSortToApiParams, deClusterArtists } from "@/lib/marketplace-sort";
import { AlbumsControllerAlbumsListSortBy } from "@/lib/lutely-api/gen/dtos/albumsControllerAlbumsListSortBy";

export default function AlbumsMarketplacePage() {
  const searchParameters = useSearch({ strict: false }) as MarketplaceSearch;
  const navigate = useNavigate();
  const isDesktopLayout = useMediaQuery("(min-width: 1024px)");
  const topAlbumsEnabled = useGateValue("top_albums");

  const { query, sort } = searchParameters;
  const search = query?.trim() ? query : undefined;

  const onSetFilter = (updates: MarketplaceUpdates) => {
    navigate({
      to: "/marketplace/albums",
      search: (old) => ({ ...old, ...updates }),
      resetScroll: false,
    });
  };

  const sortApiParams = mapSortToApiParams(sort);

  const albumsQuery = useAlbumsInfiniteList({
    pageSize: 20,
    published: true,
    search,
    sortBy: sortApiParams.sortBy as AlbumsControllerAlbumsListSortBy,
    sortDirection: sortApiParams.sortDirection,
    seed: sortApiParams.seed,
  });
  const topAlbumsQuery = useAlbumsControllerTrendingAlbums({
    query: {
      enabled: topAlbumsEnabled,
      staleTime: 5 * 60_000,
    },
  });

  const rawAlbums = albumsQuery.data?.pages.flatMap((p) => p.content) ?? [];
  const albums =
    !sort || sort === "discover" ? deClusterArtists(rawAlbums) : rawAlbums;
  const topAlbums = topAlbumsQuery.data ?? [];

  return (
    <>
      {!isDesktopLayout && (
        <Flex className="w-full flex-col gap-[12px] pt-[16px] px-4 shrink-0 mb-4">
          <MarketplaceTabs onFiltersClick={() => {}} />
        </Flex>
      )}

      <Box className={cn("w-full", !isDesktopLayout && "pb-[150px]")}>
        <FeaturedAlbumsSection />

        {topAlbumsEnabled && (
          <AlbumSliderSection albums={topAlbums} title="Trending Albums" />
        )}

        <Flex direction="column" className="pt-[40px]">
          <Flex align="center" justify="between">
            <Heading as="h2" className={marketplaceTokens.sectionHeader}>
              Albums
            </Heading>
            <SortDropdown
              value={sort}
              onChange={(value) => onSetFilter({ sort: value })}
            />
          </Flex>

          <Box
            data-testid="marketplace-albums-grid"
            className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5"
          >
            {albums.map((a) => (
              <div key={a.albumId} data-testid="marketplace-album-card">
                <AlbumCard album={a} />
              </div>
            ))}
          </Box>

          <InfiniteScrollSentinel
            hasNextPage={albumsQuery.hasNextPage}
            isFetchingNextPage={albumsQuery.isFetchingNextPage}
            fetchNextPage={albumsQuery.fetchNextPage}
          />
          {!albumsQuery.hasNextPage && albums.length > 0 && (
            <Flex className="justify-center py-6 text-md text-gray-400">
              End of list
            </Flex>
          )}
        </Flex>
      </Box>
    </>
  );
}
